/**
 * Lightweight anonymous analytics tracker.
 * - Persists a random `session_id` in localStorage.
 * - Sends "view" once per blog load.
 * - Heartbeats "engagement" every 15s while tab visible (+ on visibility change / beforeunload).
 * - Tracks max scroll depth inside the referenced content element.
 * - Marks the last-visible blog as the session "exit" on beforeunload.
 */
const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const KEY = "pl_session_id";
const HEARTBEAT_MS = 15_000;

function getSessionId() {
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(KEY, id);
  }
  return id;
}

async function post(path, body) {
  try {
    // Use fetch keepalive so beforeunload requests still land
    await fetch(`${API}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "include",
      keepalive: true,
    });
  } catch { /* silently ignore — tracking must never break the app */ }
}

export function startBlogTracker(blog, contentEl) {
  if (!blog?.blog_id) return () => {};
  const session_id = getSessionId();
  const startedAt = Date.now();
  let visibleAt = Date.now();
  let visibleTotal = 0;      // ms this tab has been visible on this blog
  let maxScroll = 0;

  // Initial view
  post("/api/events/view", {
    blog_id: blog.blog_id,
    session_id,
    referrer: document.referrer || "",
  });

  const recomputeVisible = () => {
    if (document.visibilityState === "visible") {
      visibleTotal += Date.now() - visibleAt;
      visibleAt = Date.now();
    }
  };

  const computeScrollPct = () => {
    const el = contentEl?.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const viewportBottom = window.innerHeight;
    const totalHeight = el.offsetHeight;
    if (totalHeight <= 0) return 0;
    // How much of the article has been scrolled past the fold
    const seenFromTop = Math.max(0, viewportBottom - rect.top);
    return Math.min(100, Math.round((seenFromTop / totalHeight) * 100));
  };

  const flush = () => {
    recomputeVisible();
    const seconds = Math.round(visibleTotal / 1000);
    maxScroll = Math.max(maxScroll, computeScrollPct());
    post("/api/events/engagement", {
      blog_id: blog.blog_id, session_id,
      seconds, scroll_pct: maxScroll,
    });
  };

  const onScroll = () => {
    const p = computeScrollPct();
    if (p > maxScroll) maxScroll = p;
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      visibleAt = Date.now();
    } else {
      // going hidden — accumulate + flush
      visibleTotal += Date.now() - visibleAt;
      flush();
    }
  };

  const onBeforeUnload = () => {
    recomputeVisible();
    const seconds = Math.round(visibleTotal / 1000);
    // engagement flush (keepalive fetch)
    post("/api/events/engagement", {
      blog_id: blog.blog_id, session_id,
      seconds, scroll_pct: Math.max(maxScroll, computeScrollPct()),
    });
    // mark exit
    post("/api/events/exit", { blog_id: blog.blog_id, session_id });
  };

  const heartbeat = setInterval(flush, HEARTBEAT_MS);
  window.addEventListener("scroll", onScroll, { passive: true });
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("beforeunload", onBeforeUnload);

  // Return cleanup for unmount (route change) — flush once more before letting go.
  return () => {
    clearInterval(heartbeat);
    window.removeEventListener("scroll", onScroll);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("beforeunload", onBeforeUnload);
    onBeforeUnload();
    // Reset start clock for next blog
    startedAt.valueOf(); // silence linter
  };
}
